/**
 * Product media gallery
 *  - <media-gallery>: viewer slider (scroll-snap), arrows, dots / counter, thumbnails, variant filtering & prepend
 *  - <product-deferred-media>: loads the <template> content (iframe / video / model) on demand
 *  - <product-video>: inline Shopify-hosted video with play/pause, sound toggle, timeline and muted autoplay
 */
(() => {
  const registerElement = (tag, ctor) => {
    if (!window.customElements.get(tag)) window.customElements.define(tag, ctor);
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');
  const isDisplayed = (el) => !!el && el.getClientRects().length > 0;

  /** Stops every playing media inside `scope` (videos, YouTube / Vimeo iframes, 3D models). */
  const stopMediaIn = (scope, keep) => {
    if (!scope) return;
    scope.querySelectorAll('video').forEach((video) => {
      if (keep && keep.contains(video)) return;
      if (!video.paused) video.pause();
    });
    scope.querySelectorAll('iframe').forEach((frame) => {
      if (keep && keep.contains(frame)) return;
      const target = frame.contentWindow;
      if (!target) return;
      if (frame.classList.contains('js-youtube')) {
        target.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
      } else if (frame.classList.contains('js-vimeo')) {
        target.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    });
    scope.querySelectorAll('model-viewer').forEach((viewer) => {
      if (keep && keep.contains(viewer)) return;
      if (typeof viewer.pause === 'function') viewer.pause();
    });
  };
  window.NuvoroMedia = window.NuvoroMedia || {};
  window.NuvoroMedia.stopMediaIn = stopMediaIn;

  /* ------------------------------------------------------------------ */
  /* Deferred media                                                      */
  /* ------------------------------------------------------------------ */
  class ProductDeferredMedia extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      const poster = this.querySelector(':scope > .deferred-media__poster');
      if (poster) poster.addEventListener('click', () => this.loadContent());
    }

    loadContent(moveFocus = true) {
      if (this.hasAttribute('loaded')) return null;
      const template = this.querySelector(':scope > template');
      const node = template && template.content.firstElementChild;
      if (!node) return null;

      stopMediaIn(document.body);
      const media = node.cloneNode(true);
      this.setAttribute('loaded', '');
      this.appendChild(media);

      if (media.nodeName === 'VIDEO') {
        const attempt = media.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }
      if (moveFocus && typeof media.focus === 'function') media.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('media:loaded', { bubbles: true, detail: { media } }));
      return media;
    }
  }
  registerElement('product-deferred-media', ProductDeferredMedia);

  /* ------------------------------------------------------------------ */
  /* Inline product video                                                */
  /* ------------------------------------------------------------------ */
  class ProductVideo extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.pausedByUser = false;
      this.toggleButton = this.querySelector('.product-video__toggle');
      this.soundButton = this.querySelector('.product-video__sound');
      this.timeline = this.querySelector('.product-video__timeline');

      this.handleState = this.handleState.bind(this);
      ['play', 'playing', 'pause', 'ended', 'waiting', 'canplay', 'volumechange'].forEach((type) =>
        this.video.addEventListener(type, this.handleState)
      );
      this.video.addEventListener('timeupdate', () => this.paintProgress());

      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.video.paused) {
            this.pausedByUser = false;
            this.play();
          } else {
            this.pausedByUser = true;
            this.video.pause();
          }
        });
      }

      if (this.soundButton) {
        this.soundButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.video.muted = !this.video.muted;
          if (this.video.paused && !this.video.muted) this.play();
        });
      }

      if (this.timeline) this.wireTimeline();

      if (this.autoplay) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.pausedByUser) this.play();
              else if (!entry.isIntersecting && !this.video.paused) this.video.pause();
            });
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      }
      this.handleState();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    hydrateSources() {
      let changed = false;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        changed = true;
      });
      if (changed) this.video.load();
    }

    play() {
      this.hydrateSources();
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          // autoplay with sound is blocked by most browsers: retry muted
          if (!this.video.muted) {
            this.video.muted = true;
            this.video.play().catch(() => {});
          }
        });
      }
    }

    pause() {
      this.video.pause();
    }

    handleState(event) {
      const v = this.video;
      const playing = !v.paused && !v.ended;
      this.classList.toggle('product-video--playing', playing);
      this.classList.toggle('product-video--muted', v.muted);
      if (event && event.type === 'waiting') this.classList.add('product-video--loading');
      if (!event || ['playing', 'canplay', 'pause'].includes(event.type)) this.classList.remove('product-video--loading');
      if (this.soundButton) this.soundButton.setAttribute('aria-pressed', String(!v.muted));
      if (this.toggleButton) {
        const label = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
        if (label) this.toggleButton.setAttribute('aria-label', label);
      }
    }

    paintProgress() {
      if (!this.timeline || !this.video.duration) return;
      const share = (this.video.currentTime / this.video.duration) * 100;
      this.timeline.style.setProperty('--progress', `${share}%`);
    }

    wireTimeline() {
      const seek = (clientX) => {
        const box = this.timeline.getBoundingClientRect();
        if (!box.width || !this.video.duration) return;
        const ratio = Math.min(Math.max((clientX - box.left) / box.width, 0), 1);
        this.video.currentTime = ratio * this.video.duration;
        this.paintProgress();
      };
      let dragging = false;
      this.timeline.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragging = true;
        this.timeline.setPointerCapture(event.pointerId);
        this.timeline.style.cursor = 'grabbing';
        seek(event.clientX);
      });
      this.timeline.addEventListener('pointermove', (event) => {
        if (dragging) seek(event.clientX);
      });
      const release = (event) => {
        if (!dragging) return;
        dragging = false;
        this.timeline.style.cursor = '';
        if (this.timeline.hasPointerCapture(event.pointerId)) this.timeline.releasePointerCapture(event.pointerId);
      };
      this.timeline.addEventListener('pointerup', release);
      this.timeline.addEventListener('pointercancel', release);
      this.timeline.addEventListener('click', (event) => event.stopPropagation());
    }
  }
  registerElement('product-video', ProductVideo);

  /* ------------------------------------------------------------------ */
  /* Media gallery                                                       */
  /* ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;

      this.sectionId = this.dataset.section;
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.nav = this.querySelector('[data-gallery-nav]');
      this.thumbBox = this.querySelector('[data-thumbnails]');
      this.thumbTrack = this.querySelector('[data-thumbnail-track]');
      this.status = this.querySelector('[id^="GalleryStatus-"]');
      this.allowPrepend = this.dataset.disablePrepend !== 'true';
      this.filterKey = this.dataset.filteringOption || '';
      this.activeSlide = this.track.querySelector('.product__media-item.is-active') || this.track.firstElementChild;

      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });

      if (this.nav) {
        this.prevArrow = this.nav.querySelector('.gallery-nav__arrow--prev');
        this.nextArrow = this.nav.querySelector('.gallery-nav__arrow--next');
        this.counterCurrent = this.nav.querySelector('[data-gallery-current]');
        this.counterTotal = this.nav.querySelector('[data-gallery-total]');
        if (this.prevArrow) this.prevArrow.addEventListener('click', () => this.step(-1));
        if (this.nextArrow) this.nextArrow.addEventListener('click', () => this.step(1));
        this.nav.addEventListener('click', (event) => {
          const dot = event.target.closest('.gallery-nav__dot');
          if (!dot) return;
          const slide = this.slideFor(dot.dataset.target);
          if (slide) this.scrollToSlide(slide);
        });
      }

      if (this.thumbBox) {
        this.thumbBox.addEventListener('click', (event) => {
          const button = event.target.closest('.thumbnail');
          if (button) {
            const item = button.closest('[data-target]');
            if (item) this.setActiveMedia(item.dataset.target, false);
            return;
          }
          const arrow = event.target.closest('.thumbnail-slider__button');
          if (arrow) this.scrollThumbnails(arrow.name === 'next' ? 1 : -1);
        });
        if (this.thumbTrack) {
          this.onThumbScroll = () => this.updateThumbArrows();
          this.thumbTrack.addEventListener('scroll', this.onThumbScroll, { passive: true });
        }
      }

      // opening the lightbox from a slide
      this.track.addEventListener('click', (event) => {
        const toggle = event.target.closest('.product__media-toggle');
        if (!toggle || !this.track.contains(toggle)) return;
        const modal = document.getElementById(`ProductModal-${this.sectionId}`);
        if (modal && typeof modal.show === 'function') {
          event.preventDefault();
          modal.show(toggle);
        }
      });

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.track);

      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.onBreakpoint = () => this.applyListSemantics();
      if (this.desktopQuery.addEventListener) this.desktopQuery.addEventListener('change', this.onBreakpoint);
      this.applyListSemantics();
      this.refresh();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.desktopQuery && this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener('change', this.onBreakpoint);
      }
      cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.settleTimer);
    }

    /* ---------- lookups ---------- */
    get slides() {
      return Array.from(this.track.querySelectorAll(':scope > .product__media-item'));
    }

    visibleSlides() {
      return this.slides.filter(isDisplayed);
    }

    slideFor(mediaId) {
      return this.track.querySelector(`:scope > [data-media-id="${mediaId}"]`);
    }

    thumbFor(mediaId) {
      return this.thumbTrack ? this.thumbTrack.querySelector(`[data-target="${mediaId}"]`) : null;
    }

    dotFor(mediaId) {
      return this.nav ? this.nav.querySelector(`.gallery-nav__dot[data-target="${mediaId}"]`) : null;
    }

    isSlider() {
      const overflow = window.getComputedStyle(this.track).overflowX;
      return overflow === 'auto' || overflow === 'scroll';
    }

    isRtl() {
      return window.getComputedStyle(this.track).direction === 'rtl';
    }

    /* ---------- scrolling ---------- */
    mostVisibleSlide() {
      const frame = this.track.getBoundingClientRect();
      let winner = null;
      let widest = -1;
      this.visibleSlides().forEach((slide) => {
        const box = slide.getBoundingClientRect();
        const shown = Math.min(box.right, frame.right) - Math.max(box.left, frame.left);
        if (shown > widest + 1) {
          widest = shown;
          winner = slide;
        }
      });
      return winner;
    }

    scrollToSlide(slide, behavior = scrollBehavior()) {
      if (!slide || !this.isSlider()) return;
      const frame = this.track.getBoundingClientRect();
      const box = slide.getBoundingClientRect();
      const padding = parseFloat(window.getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const delta = this.isRtl() ? box.right - frame.right + padding : box.left - frame.left - padding;
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior });
    }

    step(direction) {
      const list = this.visibleSlides();
      const current = this.isSlider() ? this.mostVisibleSlide() : this.activeSlide;
      const position = Math.max(list.indexOf(current), 0);
      const target = list[Math.min(Math.max(position + direction, 0), list.length - 1)];
      if (target && target !== current) this.scrollToSlide(target);
    }

    onTrackScroll() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const slide = this.mostVisibleSlide();
        if (slide && slide !== this.activeSlide) {
          this.markActive(slide, { scrollThumbnail: true });
          stopMediaIn(this.track, slide);
        }
        this.updateArrows();
      });
    }

    /* ---------- state ---------- */
    markActive(slide, { scrollThumbnail = true, announce = false } = {}) {
      if (!slide) return;
      this.activeSlide = slide;
      const mediaId = slide.dataset.mediaId;
      this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));

      if (this.nav) {
        this.nav.querySelectorAll('.gallery-nav__dot').forEach((dot) => {
          const on = dot.dataset.target === mediaId;
          dot.classList.toggle('is-active', on);
          if (on) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      const thumb = this.thumbFor(mediaId);
      if (this.thumbTrack) {
        this.thumbTrack.querySelectorAll('.thumbnail').forEach((button) => button.removeAttribute('aria-current'));
        if (thumb) {
          const button = thumb.querySelector('.thumbnail');
          if (button) button.setAttribute('aria-current', 'true');
          if (scrollThumbnail) this.revealThumbnail(thumb);
        }
      }

      this.updateCounter();
      this.updateArrows();
      if (announce && thumb) this.announce(slide, thumb.dataset.mediaPosition);
    }

    refresh() {
      const visible = this.visibleSlides();
      if (this.nav) {
        this.nav.querySelectorAll('.gallery-nav__dot').forEach((dot) => {
          const slide = this.slideFor(dot.dataset.target);
          dot.classList.toggle('hidden', !slide || !visible.includes(slide));
        });
      }
      if (!this.activeSlide || !visible.includes(this.activeSlide)) {
        const replacement = (this.isSlider() && this.mostVisibleSlide()) || visible[0];
        if (replacement) this.markActive(replacement, { scrollThumbnail: false });
      } else {
        this.updateCounter();
        this.updateArrows();
      }
      this.updateThumbArrows();
    }

    updateCounter() {
      if (!this.counterCurrent && !this.counterTotal) return;
      const visible = this.visibleSlides();
      if (this.counterTotal) this.counterTotal.textContent = String(visible.length);
      if (this.counterCurrent) this.counterCurrent.textContent = String(Math.max(visible.indexOf(this.activeSlide), 0) + 1);
    }

    updateArrows() {
      if (!this.prevArrow && !this.nextArrow) return;
      const visible = this.visibleSlides();
      const position = visible.indexOf(this.activeSlide);
      const start = this.isRtl() ? -this.track.scrollLeft : this.track.scrollLeft;
      const atStart = position <= 0 || start <= 1;
      const atEnd = position >= visible.length - 1 || start + this.track.clientWidth >= this.track.scrollWidth - 1;
      if (this.prevArrow) this.prevArrow.disabled = atStart;
      if (this.nextArrow) this.nextArrow.disabled = atEnd;
    }

    announce(slide, position) {
      if (!this.status || !slide.querySelector('.product__modal-opener--image img')) return;
      const template = this.dataset.announce || '';
      if (!template || !position) return;
      this.status.setAttribute('aria-hidden', 'false');
      this.status.textContent = template.replace('[index]', position);
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => this.status.setAttribute('aria-hidden', 'true'), 2000);
    }

    /* ---------- thumbnails ---------- */
    thumbsVertical() {
      if (!this.thumbTrack) return false;
      return window.getComputedStyle(this.thumbTrack).flexDirection === 'column';
    }

    revealThumbnail(thumb) {
      if (!this.thumbTrack || !isDisplayed(thumb)) return;
      const frame = this.thumbTrack.getBoundingClientRect();
      const box = thumb.getBoundingClientRect();
      const inset = 8;
      if (this.thumbsVertical()) {
        if (box.top < frame.top || box.bottom > frame.bottom) {
          this.thumbTrack.scrollTo({ top: this.thumbTrack.scrollTop + box.top - frame.top - inset, behavior: scrollBehavior() });
        }
      } else if (box.left < frame.left || box.right > frame.right) {
        this.thumbTrack.scrollTo({ left: this.thumbTrack.scrollLeft + box.left - frame.left - inset, behavior: scrollBehavior() });
      }
    }

    scrollThumbnails(direction) {
      if (!this.thumbTrack) return;
      const first = Array.from(this.thumbTrack.children).find(isDisplayed);
      if (!first) return;
      const vertical = this.thumbsVertical();
      const gap = parseFloat(window.getComputedStyle(this.thumbTrack)[vertical ? 'rowGap' : 'columnGap']) || 0;
      const size = (vertical ? first.offsetHeight : first.offsetWidth) + gap;
      const distance = size * 3 * direction * (this.isRtl() && !vertical ? -1 : 1);
      this.thumbTrack.scrollBy({ [vertical ? 'top' : 'left']: distance, behavior: scrollBehavior() });
    }

    updateThumbArrows() {
      if (!this.thumbBox || !this.thumbTrack) return;
      const prev = this.thumbBox.querySelector('.thumbnail-slider__button--prev');
      const next = this.thumbBox.querySelector('.thumbnail-slider__button--next');
      const vertical = this.thumbsVertical();
      const offset = Math.abs(vertical ? this.thumbTrack.scrollTop : this.thumbTrack.scrollLeft);
      const room = vertical
        ? this.thumbTrack.scrollHeight - this.thumbTrack.clientHeight
        : this.thumbTrack.scrollWidth - this.thumbTrack.clientWidth;
      if (prev) prev.disabled = offset <= 1;
      if (next) next.disabled = offset >= room - 1;
    }

    /* ---------- public API (used by <product-info>) ---------- */
    applyVariantFilter(variant) {
      if (!this.filterKey || !variant) return;
      const wanted = variant[this.filterKey];
      this.querySelectorAll('.product__media-item, .thumbnail-list__item, .gallery-nav__dot').forEach((node) => {
        const keep = node.dataset.alt === wanted || node.dataset.alt === 'always_display';
        node.classList.toggle('hidden', !keep);
      });
    }

    setActiveMedia(mediaId, prepend = false, filtering = false, variant = null) {
      if (filtering && variant) this.applyVariantFilter(variant);
      const slide = this.slideFor(mediaId);
      if (!slide) {
        this.refresh();
        return;
      }

      if (prepend && this.allowPrepend) {
        this.track.prepend(slide);
        const thumb = this.thumbFor(mediaId);
        if (thumb) thumb.parentElement.prepend(thumb);
        const dot = this.dotFor(mediaId);
        if (dot) dot.parentElement.prepend(dot);
        this.track.scrollTo({ left: 0, behavior: 'auto' });
      }

      this.markActive(slide, { scrollThumbnail: true, announce: true });
      this.refresh();
      this.keepHeaderStill();

      requestAnimationFrame(() => {
        if (this.isSlider()) {
          this.scrollToSlide(slide);
        } else {
          const box = slide.getBoundingClientRect();
          const outOfView = box.top < 0 || box.bottom > window.innerHeight;
          if (outOfView) slide.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        }
      });

      stopMediaIn(this.track, slide);
      const deferred = slide.querySelector('product-deferred-media, product-model');
      if (deferred && typeof deferred.loadContent === 'function' && isDisplayed(deferred)) deferred.loadContent(false);
    }

    keepHeaderStill() {
      const header = document.querySelector('sticky-header');
      if (header) header.dispatchEvent(new Event('preventHeaderReveal'));
    }

    applyListSemantics() {
      const layout = this.dataset.desktopLayout || '';
      const presentational = layout.includes('thumbnail') && this.desktopQuery && this.desktopQuery.matches;
      if (presentational) {
        this.track.setAttribute('role', 'presentation');
        this.slides.forEach((slide) => slide.setAttribute('role', 'presentation'));
      } else {
        this.track.setAttribute('role', 'list');
        this.slides.forEach((slide) => slide.removeAttribute('role'));
      }
    }
  }
  registerElement('media-gallery', MediaGallery);
})();
